import React, { Fragment, FunctionComponent } from "react";

import { Language, useLanguage } from "../../../../i18n/useLanguage";
import {
  PricingContext,
  PromotionLink,
  TirePartItem,
} from "../../../../queries/useSearchTiresByVehicle";
import { Dropdown } from "../../../../ui/Dropdown";
import { Toggle } from "../../../../ui/Toggle";
import { ROLLOVER } from "../../../../ui/Toggle/useToggle";

type PromotionLinksProps = {
  part: TirePartItem | null;
  quantity?: string;
};

export const useRebateLinks = (
  language: Language,
  link: [] | PromotionLink = [],
  quantity?: string,
): PricingContext[] => {
  const partPricingItem =
    !link || !quantity || Array.isArray(link) ? null : link[quantity];

  const options: PricingContext[] =
    partPricingItem?.contextList.filter(({ linkText }) =>
      Boolean(linkText?.[language] || linkText?.en),
    ) || [];

  return options;
};

export const Rebates: FunctionComponent<PromotionLinksProps> = ({
  part,
  quantity,
}) => {
  const [language] = useLanguage();

  const rebates = useRebateLinks(
    language,
    part?.partPricing.primaryPart.link,
    quantity,
  );

  return rebates.length ? (
    <h5 className="rebates">
      {rebates.map(
        (
          { linkBody, linkText, linkURL, promotionPricingText, promotionText },
          key,
        ) => {
          const href = linkURL?.[language] || linkURL?.en;
          const linkName = linkText?.[language] || linkText?.en;
          const linkDescription = linkBody?.[language] || linkBody?.en;

          const promotionName = promotionText?.[language] || promotionText?.en;
          const promotionDescription =
            promotionPricingText?.[language] || promotionPricingText?.en;

          const buttonId = `rebate-button-${key}`;

          return (
            <Fragment key={key}>
              {key > 0 && <div className="mx-1 lg:mx-2">&middot;</div>}
              {linkText && href ? (
                <Toggle
                  disabled={!linkDescription || linkDescription === linkName}
                  mode={ROLLOVER}
                >
                  <a
                    className="flex justify-between items-center underline"
                    href={href}
                    id={buttonId}
                    key={key}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {linkName}
                  </a>
                  <Dropdown
                    aria-labelledby={buttonId}
                    id={`rebate-dropdown-${key}`}
                    preferredPlacement="bottom"
                    role="menu"
                  >
                    <div className="rebates-tooltip">
                      <h6>{linkName}</h6>
                      <p>{linkDescription}</p>
                    </div>
                  </Dropdown>
                </Toggle>
              ) : (
                <Toggle
                  disabled={
                    !promotionDescription ||
                    promotionDescription === promotionName
                  }
                  mode={ROLLOVER}
                >
                  <span
                    className="flex justify-between items-center"
                    id={buttonId}
                    key={key}
                  >
                    {promotionName}
                  </span>
                  <Dropdown
                    aria-labelledby={buttonId}
                    id={`rebate-dropdown-${key}`}
                    preferredPlacement="bottom"
                    role="menu"
                  >
                    <div className="rebates-tooltip">
                      <h6>{promotionName}</h6>
                      <p>{promotionDescription}</p>
                    </div>
                  </Dropdown>
                </Toggle>
              )}
            </Fragment>
          );
        },
      )}
    </h5>
  ) : null;
};
